import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { openRazorpay } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, CreditCard, AlertCircle, Loader2, Calendar, Sparkles, Tag, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function SettingsBilling({ compact = false }) {
  const { user, refreshBilling } = useAuth();
  const [status, setStatus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [coupon, setCoupon] = useState(null); // {code, final_price_usd, original_price_usd, discount_type, discount_value}

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch { setStatus(null); }
  };

  useEffect(() => { load(); }, []);

  const applyCoupon = async (e) => {
    e?.preventDefault?.();
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.post("/billing/validate-coupon", { code });
      if (data.valid) {
        setCoupon(data);
        toast.success(`Coupon applied — you'll pay $${data.final_price_usd}`);
      } else {
        setCoupon(null);
        toast.error(data.error || "Invalid coupon");
      }
    } catch (err) {
      setCoupon(null);
      toast.error(formatErr(err.response?.data?.detail) || "Could not validate coupon");
    } finally { setApplying(false); }
  };

  const clearCoupon = () => { setCoupon(null); setCodeInput(""); };

  const subscribe = async () => {
    setPaying(true);
    try {
      const payload = coupon ? { coupon_code: coupon.code } : {};
      const { data: order } = await api.post("/billing/checkout", payload);
      await openRazorpay({
        order: { ...order, description: "PlumberHQ monthly subscription" },
        prefill: { name: user?.full_name, email: user?.email },
        onSuccess: async (resp) => {
          try {
            await api.post("/billing/verify", resp);
            toast.success("Subscription active — thank you!");
            clearCoupon();
            await load();
            await refreshBilling?.();
          } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || "Could not verify payment");
          }
        },
        onFailure: (err) => toast.error(err?.description || err?.message || "Payment failed"),
      });
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail);
      toast.error(msg || "Could not start checkout");
      // If coupon became invalid (e.g. hit max uses between apply + click), refresh state.
      if (msg && /coupon/i.test(msg)) clearCoupon();
    } finally { setPaying(false); }
  };

  const nextBilling = status?.subscription_ends_at || status?.trial_ends_at;
  const chargeAmt = coupon ? coupon.final_price_usd : status?.price_usd;

  const body = (
    <div className={compact ? "" : "bg-white rounded-2xl border border-slate-200 p-5 sm:p-6"}>
      {status === null ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs text-slate-500 font-bold uppercase tracking-wide">PlumberHQ Plan</div>
              <div className="mt-1 flex items-baseline gap-2">
                {coupon ? (
                  <>
                    <span className="text-lg text-slate-400 line-through" data-testid="plan-original-price">${status.price_usd}</span>
                    <span className="text-3xl font-bold text-green-600" style={{ fontFamily: "Outfit" }} data-testid="plan-final-price">${coupon.final_price_usd}</span>
                    <span className="text-sm text-slate-500">/mo</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="plan-price">${status.price_usd}/mo</span>
                )}
              </div>
            </div>
            <StatusPill status={status.status} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <InfoTile icon={Calendar} label={status.status === "active" ? "Renews on" : "Trial ends"} value={nextBilling ? new Date(nextBilling).toLocaleDateString("en-US") : "—"} testId="next-billing" />
            <InfoTile icon={Sparkles} label="Days remaining" value={status.days_remaining} testId="days-remaining" />
          </div>

          {status.needs_payment && (
            <div className={`rounded-xl p-3 flex items-start gap-2 text-sm ${status.expired ? "bg-red-50 border border-red-200 text-red-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`} data-testid="billing-warning">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <b>{status.expired ? "Your trial has ended" : "Renew soon"}</b>
                <div className="text-xs mt-0.5">
                  {status.expired ? "Subscribe to continue using PlumberHQ." : `Only ${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"} left — subscribe to avoid interruption.`}
                </div>
              </div>
            </div>
          )}

          {/* Coupon input */}
          {!coupon ? (
            <form onSubmit={applyCoupon} className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs text-slate-600 font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Have a coupon?
              </div>
              <div className="flex gap-2">
                <Input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="ENTER CODE"
                  className="h-11 font-mono tracking-wide uppercase"
                  disabled={applying}
                  data-testid="coupon-input"
                />
                <Button type="submit" variant="outline" disabled={applying || !codeInput.trim()} className="h-11 px-4 rounded-xl font-semibold" data-testid="coupon-apply">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 flex items-start gap-2" data-testid="coupon-applied">
              <div className="h-8 w-8 rounded-lg bg-green-600 text-white flex items-center justify-center flex-shrink-0"><Check className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-green-900 font-mono" data-testid="coupon-applied-code">{coupon.code}</div>
                <div className="text-xs text-green-800">
                  {coupon.discount_type === "percent" ? `${coupon.discount_value}% off` : `$${coupon.discount_value} off`} — you save ${(status.price_usd - coupon.final_price_usd).toFixed(2)}
                </div>
              </div>
              <button type="button" onClick={clearCoupon} className="text-green-800 hover:text-green-900" data-testid="coupon-remove" aria-label="Remove coupon">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <Button onClick={subscribe} disabled={paying || !status.configured} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="subscribe-btn">
            {paying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening Razorpay…</> : <><CreditCard className="w-4 h-4 mr-1.5" /> {status.status === "active" ? `Renew — $${chargeAmt}` : `Subscribe — $${chargeAmt}/mo`}</>}
          </Button>

          {!status.configured && (
            <div className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="billing-not-configured">
              <b>Not yet available.</b> The platform owner must set <code>PLATFORM_RAZORPAY_KEY_ID</code> and <code>PLATFORM_RAZORPAY_KEY_SECRET</code> in <code>backend/.env</code> to enable subscription payments.
            </div>
          )}

          <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            Charges via Razorpay Checkout. Each payment adds 30 days to your subscription. Safe secure checkout — cards, UPI, wallets.
          </div>
        </div>
      )}
    </div>
  );

  if (compact) return body;
  return (
    <div className="space-y-5 max-w-2xl">
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
      <h1 className="text-3xl font-bold">Settings · Billing</h1>
      {body}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    trial: { label: "Free Trial", cls: "bg-blue-100 text-blue-800 ring-blue-200" },
    active: { label: "Active", cls: "bg-green-100 text-green-800 ring-green-200" },
    expired: { label: "Expired", cls: "bg-red-100 text-red-800 ring-red-200" },
    admin: { label: "Admin", cls: "bg-slate-900 text-white ring-slate-900" },
  };
  const s = map[status] || map.trial;
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 ring-inset ${s.cls}`} data-testid={`billing-status-${status}`}>{s.label}</span>;
}

function InfoTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}
